import * as React from "react";
import * as Sentry from "@sentry/browser";
import axios, {AxiosResponse, Axios, AxiosError, AxiosInstance} from "axios";
import {ReactNode, useEffect} from "react";
import {getRoute} from "../utils/routes";
import {AuthContext, JWTToken, /*RetryQueueItem, */ User} from "./interfaces";
import {Timer} from "../shared/utils";
import {Button, message} from "antd";
import {parseResponseError} from "../utils/message";
import {QueryClient, useQuery} from "@tanstack/react-query";
import {apiFetcher} from "../fetchers/apiFetcher.tsx";
import {differenceInSeconds} from "date-fns";
import {useNetworkState} from "@uidotdev/usehooks";

const LOCAL_STORAGE_AUTH_TOKEN = 'LOCAL_STORAGE_AUTH_TOKEN';

const authContext = React.createContext<AuthContext>({} as AuthContext);


export enum PlanFeatures {
    CHAT = 'chat_widgets',
    DOCUMENTS = 'documents',
    PAYMENT_SERVICES = 'payment_services',
    PERFECTOMY_TRANSCRIBE = 'perfectomy_transcribe',
    PERFECTOMY_ASSISTANT = 'perfectomy_assistant',
    EMAIL_MARKETING = 'email_marketing',
    PHONE = 'phone_plan',
    MESSAGING_APPS = 'messaging_apps'
}


export interface PaginatedResponse<T> {
    next: string | null,
    results: T[],
    previous: string | null,
    loading?: boolean,
    error?: string | null,
    total?: number,
    count?: number,
    total_unread?: number,
}


export interface ResponseState<T> {
    data: T,
    loading: boolean,
    error?: string | null
}


const ProvideAuth: React.FunctionComponent<{
    children: ReactNode
}> = ({children}) => {
    const auth = useProvideAuth();

    return (
        <authContext.Provider value={auth}>
            {children}
        </authContext.Provider>
    );
};

function useAuth() {
    return React.useContext(authContext);
}

const queryClient = new QueryClient();

const instances: { [token: string]: { instance: AxiosInstance, interceptor: number } } = {};

function parseJwt(token: string) {
    var base64Url = token.split('.')[1];
    var base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    var jsonPayload = decodeURIComponent(window.atob(base64).split('').map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

function useProvideAuth(): AuthContext {
    const [user, setUser] = React.useState<User>({} as User);
    const [jwtToken, setJwtToken] = React.useState<JWTToken>();
    const [loading, setLoading] = React.useState(true);
    const [newAppUpdate, setNewAppUpdate] = React.useState(false);
    const [clientVersionFromServer, setClientVersionFromServer] = React.useState<string>();
    const network = useNetworkState();
    const mounted = React.useRef(false);

    useEffect(() => {
        mounted.current = true;

        return () => {
            mounted.current = false;
        }
    }, []);

    const axiosInstance: (authToken?: string) => Axios = (authToken) => {
        const csrfTokenWrapper = document.querySelector('#csrf-token input[name="csrfmiddlewaretoken"]');
        const token = csrfTokenWrapper?.getAttribute('value');
        let currentToken;
        let finalToken;
        if (!jwtToken) {
            const storedToken = localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN);
            if (!storedToken) {
                currentToken = null;
            } else {
                try {
                    let token = JSON.parse(storedToken);
                    currentToken = token.access_token;
                } catch (err) {
                    currentToken = null;
                }
            }
        } else {
            currentToken = jwtToken.access_token;
        }

        const headers: {
            [key: string]: string
        } = {}
        if (token) {
            headers['X-CSRFToken'] = token;
        }
        if (authToken) {
            finalToken = authToken;
        } else if (currentToken) {
            finalToken = currentToken;
        }

        // const refreshAndRetryQueue: RetryQueueItem[] = [];
        // let retried = false;

        const updateInterceptor = (response: AxiosResponse) => {
            if (mounted.current) {
                setClientVersionFromServer(response.headers['x-client-version']);
            }
            return response
        }

        // const refreshTokenInterceptor = async (err: AxiosError<any>) => {
        //     const errorDetails = err.response?.data?.error?.details;
        //     if (!!err.response && err.response.status === 401) {
        //         const {config: originalConfig} = err;
        //         const storedToken = localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN);
        //         if (!storedToken || !originalConfig) {
        //             throw err;
        //         } else {
        //             try {
        //                 const {refresh_token} = JSON.parse(storedToken);
        //                 const response = await axios.post<{
        //                     access: string,
        //                     access_expiration: string,
        //                 }>(getRoute("basicAuth.refreshToken"), {refresh: refresh_token});
        //                 originalConfig.headers['authorization'] = 'Bearer ' + response.data.access;
        //                 localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, JSON.stringify({
        //                     access_token: response.data.access,
        //                     refresh_token: refresh_token,
        //                 }));
        //                 setJwtToken({
        //                     access_token: response.data.access,
        //                     refresh_token: refresh_token,
        //                 });
        //                 refreshAndRetryQueue.forEach(({config, resolve, reject}) => {
        //                     instance
        //                         .request(config)
        //                         .then((response) => resolve(response))
        //                         .catch((err) => reject(err));
        //                 });
        //
        //                 // Clear the queue
        //                 refreshAndRetryQueue.length = 0;
        //
        //                 // Retry the original request
        //                 return instance(originalConfig);
        //             } catch (e) {
        //
        //             }
        //         }
        //         if (originalConfig && !retried) {
        //             localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
        //             const delayRetryRequest = new Promise<void>((resolve) => {
        //                 setTimeout(() => {
        //                     resolve();
        //                 }, 1000);
        //             });
        //             retried = true;
        //             delete originalConfig.headers['authorization']
        //             return delayRetryRequest.then(() => axios(originalConfig));
        //         }
        //         setLoading(true);
        //         setUser({} as User);
        //         localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
        //         throw err
        //     } else if (!!err.response && err.response.status === 403 && errorDetails && errorDetails.detail.indexOf("CSRF Failed") !== -1) {
        //         // Renew CSRF
        //         window.location.reload()
        //     }
        //     return Promise.reject(err)
        // }


        if (instances[finalToken]) {
            const oldInterceptor = instances[finalToken].interceptor;
            const instance = instances[finalToken].instance;
            instance.interceptors.response.eject(oldInterceptor);
            instance.interceptors.response.use(
                updateInterceptor,
                // refreshTokenInterceptor
            );
            return instances[finalToken].instance;
        }

        if (finalToken) {
            headers['Authorization'] = 'Bearer ' + finalToken;
        }

        const instance = axios.create({headers});

        const interceptor = instance.interceptors.response.use(
            updateInterceptor,
            // refreshTokenInterceptor
        );

        instances[finalToken] = {
            instance,
            interceptor
        };
        return instance;
    };

    const userQuery = useQuery({
        queryKey: ['user', jwtToken?.access_token],
        queryFn: apiFetcher<any>(
            getRoute("basicAuth.user"),
            axiosInstance(jwtToken?.access_token),
        ),
        enabled: !!jwtToken?.access_token,
        retry: (failureCount, error) => {
            if (error instanceof AxiosError && error.response && jwtToken) {
                const parsed = parseJwt(jwtToken.access_token);
                const expiresIn = differenceInSeconds(new Date(parsed.exp * 1000), new Date());
                if (expiresIn < 0 && error.response.status === 401) {
                    return true;
                }
                if (error.response.status === 401) {
                    localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
                    setJwtToken(undefined);
                }
                return false;
            }
            return failureCount < 4;
        },
    }, queryClient)

    const login = async (email: string, password: string) => {
        const authRoute = getRoute("basicAuth.login");
        localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN)

        return new Promise<void>((resolve, reject) => {
            axiosInstance().post<{
                access: string,
                refresh: string,
                user: User,
            }>(authRoute, {
                email,
                password
            }).then(response => {
                localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, JSON.stringify({
                    access_token: response.data.access,
                    refresh_token: response.data.refresh,
                }));
                setJwtToken({
                    access_token: response.data.access,
                    refresh_token: response.data.refresh,
                });
                resolve()
            }).catch(err => {
                reject(err)
            });
        });

    };

    const socialLogin = async (code: string, provider: string) => {
        const authRoute = getRoute("basicAuth.socialLogin", {provider});
        localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN)

        return new Promise<void>((resolve, reject) => {
            axiosInstance().post<{
                access: string,
                refresh: string,
                user: User,
            }>(authRoute, {
                code
            }).then(response => {
                localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, JSON.stringify({
                    access_token: response.data.access,
                    refresh_token: response.data.refresh,
                }));
                setJwtToken({
                    access_token: response.data.access,
                    refresh_token: response.data.refresh,
                })
                resolve();
            }).catch(err => {
                reject(err)
            });
        });
    }

    const logout = () => {
        return new Promise<null>(((resolve, reject) => {
            axiosInstance().post(getRoute("basicAuth.logout"))
                .then(_ => {
                    setUser({} as User);
                    localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
                    Sentry.setUser(null);
                    resolve(null);
                })
                .catch((err) => {
                    if (err.response?.status === 401) {
                        localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
                        setUser({} as User);
                        Sentry.setUser(null);
                        resolve(null);
                    }
                    reject(err);
                })
        }));
    };

    const storageLogin = () => {
        return new Promise<void>((resolve, reject) => {
            const token = localStorage.getItem('LOCAL_STORAGE_AUTH_TOKEN');
            if (token) {
                try {
                    setJwtToken(JSON.parse(token));
                    resolve();
                    // getCurrentUser(access_token).then((response) => {
                    //     resolve(response.data);
                    // }).catch(reject)
                } catch (err) {
                    localStorage.removeItem(LOCAL_STORAGE_AUTH_TOKEN);
                    reject(err);
                }
            } else {
                reject(new Error('No token available'));
            }
        });
    };

    const convertUser = (currentUser: any) => {
        const newUser = {...currentUser};
        newUser.permissions = newUser.account_settings
            .map((permission: any) => ({[permission.account]: permission.permissions}))
            .reduce((acc: string, object: string) => Object.assign({}, acc, object), {});
        newUser.account_settings = newUser.account_settings.map((account_permission: any) => ({
            [account_permission.account]: account_permission
        })).reduce((acc: any, object: any) => Object.assign({}, acc, object), {});
        return newUser;
    }

    useEffect(() => {
        if (userQuery.isFetching) {
            return;
        }
        if (jwtToken?.access_token) {
            const parsed = parseJwt(jwtToken.access_token);
            const expiresIn = differenceInSeconds(new Date(parsed.exp * 1000), new Date());
            if (expiresIn < 0) {
                return;
            }
        }
        if (!userQuery.data?.data) {
            setUser({} as User);
            return;
        }
        Sentry.setUser({
            id: userQuery.data.data.id
        })
        setUser(convertUser(userQuery.data?.data));
        setLoading(false);
    }, [userQuery.data?.data, jwtToken?.access_token]);

    useEffect(() => {
        if (!jwtToken || !network.online) return;
        const parsed = parseJwt(jwtToken.access_token);
        const expiresIn = differenceInSeconds(new Date(parsed.exp * 1000), new Date());
        const timeout = setTimeout(async () => {
            const storedToken = localStorage.getItem(LOCAL_STORAGE_AUTH_TOKEN);
            if (!storedToken) {
                await logout();
                return;
            }
            const {refresh_token} = JSON.parse(storedToken);
            try {
                const response = await axios.post<{
                    access: string,
                    access_expiration: string,
                }>(getRoute("basicAuth.refreshToken"), {refresh: refresh_token});
                localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, JSON.stringify({
                    access_token: response.data.access,
                    refresh_token: refresh_token,
                }));
                setJwtToken({
                    access_token: response.data.access,
                    refresh_token: refresh_token,
                });
            } catch (error) {
                await logout();
            }
        }, (expiresIn * 1000) / 2);

        return () => {
            clearTimeout(timeout);
        }
    }, [jwtToken, network.online]);

    useEffect(() => {
        if (!!clientVersionFromServer && clientVersionFromServer !== import.meta.env.VITE_APP_VERSION) {
            setNewAppUpdate(() => {
                return true;
            });
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            window.appUpdate = true;
        }
    }, [clientVersionFromServer])

    const getCurrentUser: ((token?: string) => Promise<AxiosResponse>) = (token) => {
        if (!token && !jwtToken) {
            return new Promise((_, reject) => reject());
        }
        const promise = axiosInstance(token || jwtToken?.access_token).get(getRoute("basicAuth.user"));
        promise.then(response => {
            const newUser = {
                ...response.data,
                token: token,
            };
            setUser(convertUser(newUser));
            setLoading(false)
        }).catch(err => {
            console.log(err)
            setLoading(false)
        });
        return promise;
    };

    const signup: (data: {
        password1: string
        password2: string
        email: string,
        first_name: string,
        last_name: string
    }) => Promise<any> = (data) => {
        return axiosInstance().post(getRoute("basicAuth.register"), {
            ...data,
            username: data.email
        })
    }

    useEffect(() => {
        storageLogin().catch(() => {
            setLoading(false);
        });
    }, []);

    const updateUser = React.useCallback((user: Partial<User>) => {
        setUser(currentUser => ({
            ...currentUser,
            ...user
        }))
    }, []);

    return {
        user,
        login,
        socialLogin,
        logout,
        storageLogin,
        loading,
        axiosInstance,
        signup,
        newAppUpdate,
        updateUser,
        getCurrentUser,
        convertUser,
        reloadUser: userQuery.refetch,
        token: jwtToken
    };
}

export const ResendVerificationEmailButton: React.FunctionComponent<{
    email: string
}> = ({email}) => {
    const {axiosInstance} = useAuth();
    const [resendingEmail, setResendingEmail] = React.useState(false);
    const [lockedResendEmail, setLockedResendEmail] = React.useState(false);

    const resendVerificationEmail = React.useCallback(() => {
        setResendingEmail(true);
        axiosInstance().post(getRoute("basicAuth.resendVerificationEmail"), {email: email.toLowerCase()})
            .then(() => {
                setResendingEmail(false);
                setLockedResendEmail(true);
                message.success("Email has been resent").then();
            })
            .catch(err => {
                message.error(parseResponseError(err)).then();
                setResendingEmail(false);
            })
    }, [axiosInstance, email]);

    return (
        <Button
            type="text"
            style={{background: "white"}}
            onClick={resendVerificationEmail}
            loading={resendingEmail}
            disabled={resendingEmail || lockedResendEmail}
        >
            {lockedResendEmail ? (
                <Timer seconds={120} onFinish={() => setLockedResendEmail(false)}/>
            ) : 'Resend verification E-Mail'}
        </Button>
    )
}

export const accountHasFeature = (user: User, accountId: string | number, feature: PlanFeatures) => {
    const accountSettings = user.account_settings[accountId];
    if (!accountSettings) return false;
    const plan = accountSettings.plan_subscription;
    if (!plan) return false;
    if (plan.status !== "active" && plan.status !== "incomplete" && plan.status !== "trailing" && plan.status !== "past_due") return false;
    return plan.features?.indexOf(feature) !== -1
}

export const userHasTour = (user: User, tour: string) => {
    return user.tours ? user.tours.includes(tour) : false;
}

export const userHasPermission = (user: User, accountId: string | number, permission: string) => {
    const accountSettings = user.account_settings[accountId];
    if (!accountSettings) return false;
    return accountSettings.permissions.indexOf(permission) !== -1;
}

export const userHasAccess= (user: User, accountId: string | number, access: string) => {
    const accountSettings = user.account_settings[accountId];
    if (!accountSettings) return false;
    const accessSet = new Set(accountSettings.permissions.map((permission: string) => permission.split(".")[0]));
    return accessSet.has(access);
}

export {
    ProvideAuth,
    useAuth
};

export default ProvideAuth;